import React, { useState, useEffect } from 'react';
import { Heart, TrendingUp, Target, CheckCircle, Sparkles, Star, Trophy, Zap, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';
import UpgradeModal from '../components/UpgradeModal';
import TrialBanner from '../components/TrialBanner';
import TrialExpiredModal from '../components/TrialExpiredModal';
import EmailVerificationBanner from '../components/EmailVerificationBanner';

const Home = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [currentMood, setCurrentMood] = useState(null);
  const [currentAffirmation, setCurrentAffirmation] = useState(null);
  const [microAction, setMicroAction] = useState(null);
  const [loveScore, setLoveScore] = useState({ current_score: 0, level: 1, progress_to_next_level: {} });
  const [streak, setStreak] = useState({ currentStreak: 0, totalCheckins: 0, longestStreak: 0 });
  const [isGeneratingAffirmation, setIsGeneratingAffirmation] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [todayStats, setTodayStats] = useState({ 
    activities: 0, 
    loveScoreEarned: 0,
    journalEntries: 0,
    gardenSessions: 0,
    companionChats: 0
  });
  const [blueprint, setBlueprint] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const moodOptions = [
    { value: 1, emoji: '😔', label: 'Sad', color: 'from-gray-400 to-gray-600', description: 'Having a tough time' },
    { value: 2, emoji: '😐', label: 'Okay', color: 'from-blue-400 to-blue-600', description: 'Getting through the day' },
    { value: 3, emoji: '🙂', label: 'Good', color: 'from-green-400 to-green-600', description: 'Feeling positive' },
    { value: 4, emoji: '😊', label: 'Great', color: 'from-yellow-400 to-yellow-600', description: 'Really good day!' },
    { value: 5, emoji: '🤩', label: 'Empowered', color: 'from-purple-400 to-purple-600', description: 'On top of the world!' }
  ];

  // Load initial data
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      // Load blueprint if user completed onboarding
      try {
        const blueprintResponse = await fetch(`${API_BASE_URL}/api/onboarding/blueprint`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (blueprintResponse.ok) {
          const data = await blueprintResponse.json();
          if (data.has_blueprint) {
            setBlueprint(data.blueprint);
          }
        }
      } catch (error) {
        console.log('Blueprint not loaded:', error);
      }

      // Load subscription status (includes trial info)
      const subscriptionResponse = await fetch(`${API_BASE_URL}/api/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (subscriptionResponse.ok) {
        const subData = await subscriptionResponse.json();
        setSubscriptionInfo(subData);
        
        // Show trial expired modal if just downgraded (show once per session)
        if (subData.plan === 'reflect' && subData.status === 'active' && !sessionStorage.getItem('trial_expired_shown')) {
          const wasOnTrial = localStorage.getItem('was_on_trial');
          if (wasOnTrial === 'true') {
            setShowTrialExpiredModal(true);
            sessionStorage.setItem('trial_expired_shown', 'true');
            localStorage.removeItem('was_on_trial');
          }
        }
        
        // Track if user is on trial for future detection
        if (subData.plan === 'growth_trial') {
          localStorage.setItem('was_on_trial', 'true');
        }
      }

      // Load REAL Love Score data (not test data)
      const loveScoreResponse = await fetch(`${API_BASE_URL}/api/love-score`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (loveScoreResponse.ok) {
        const loveScoreData = await loveScoreResponse.json();
        setLoveScore(loveScoreData);
      }

      // Load REAL streak data
      if (user?.id) {
        const streakResponse = await fetch(`${API_BASE_URL}/api/streaks/${user.id}`);
        if (streakResponse.ok) {
          const streakData = await streakResponse.json();
          setStreak(streakData);
        }
      }

      // Load REAL today's activities
      loadTodayStats();

    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadTodayStats = async () => {
    // For now, calculate from real data - implement proper analytics later
    setTodayStats({
      activities: (currentAffirmation ? 1 : 0) + (streak.currentStreak > 0 ? 1 : 0),
      loveScoreEarned: 0, // Will be updated as user earns points
      journalEntries: 0,
      gardenSessions: 0,
      companionChats: 0
    });
  };

  const generatePersonalizedAffirmation = async (mood) => {
    if (!mood) return;
    
    setIsGeneratingAffirmation(true);
    try {
      // Generate AI-powered SHORT affirmation using dedicated endpoint
      const response = await fetch(`${API_BASE_URL}/api/affirmations/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mood: mood,
          userId: user.id
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        setCurrentAffirmation({
          id: data.id,
          affirmation: data.affirmation,
          mood: mood,
          createdAt: new Date(data.timestamp)
        });

        setMicroAction(data.action);
        
        // Award Love Score for daily affirmation
        await awardLoveScore(15, 'daily_affirmation', 'Generated personalized daily affirmation');
        
        toast({
          title: "✨ Your Personal Affirmation is Ready!",
          description: "Crafted just for you based on your mood 💕",
          duration: 4000,
        });
      } else if (response.status === 402) {
        // Payment Required - show upgrade modal
        const errorData = await response.json();
        setShowUpgradeModal(true);
        toast({
          title: "🌟 Upgrade to Growth Plan",
          description: errorData.detail?.message || "You've reached your free plan limit",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Error generating affirmation:', error);
      // Fallback affirmation
      setCurrentAffirmation({
        id: Date.now().toString(),
        affirmation: "You are worthy of love, especially from yourself. Today is another chance to honor your amazing heart.",
        mood: mood,
        createdAt: new Date()
      });
      setMicroAction("Take three deep breaths and say something kind to yourself in the mirror.");
      
      toast({
        title: "💕 Your Affirmation is Ready",
        description: "A gentle reminder of your worth",
        duration: 3000,
      });
    } finally {
      setIsGeneratingAffirmation(false);
    }
  };

  const awardLoveScore = async (amount, source, description) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/love-score/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          source: source,
          description: description
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setTodayStats(prev => ({
          ...prev,
          loveScoreEarned: prev.loveScoreEarned + amount
        }));
        
        // Reload Love Score data
        loadUserData();
      }
    } catch (error) {
      console.error('Error awarding Love Score:', error);
    }
  };

  const handleMoodSelect = (mood) => {
    setCurrentMood(mood);
    generatePersonalizedAffirmation(mood.value);
  };

  const saveToLoveVault = async () => {
    if (!currentAffirmation) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/affirmations/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          affirmationId: currentAffirmation.id,
          userId: user.id,
          affirmation: currentAffirmation.affirmation
        }),
      });

      if (response.ok) {
        toast({
          title: "✨ Added to your Love Vault",
          description: "A reminder of your worth, always waiting for you 💕",
          duration: 3000,
        });
        
        // Award Love Score for saving affirmation
        await awardLoveScore(10, 'save_affirmation', 'Saved affirmation to Love Vault');
      }
    } catch (error) {
      toast({
        title: "Oops! Something's missing. Let's try again 💕",
        description: "",
        variant: "destructive",
      });
    }
  };

  const markActionComplete = async () => {
    toast({
      title: "🌟 Beautiful work!",
      description: "You're rewiring daily — small actions, big transformations 💖",
      duration: 3000,
    });
    
    // Award Love Score for completing micro action
    await awardLoveScore(5, 'micro_action', 'Completed daily micro action');
  };

  // Helper functions for gamification
  const getLevelName = (level) => {
    const levelNames = {
      1: "Tiny Seed", 2: "First Sprout", 3: "Growing Stem", 4: "Early Leaves", 5: "Budding Promise",
      6: "First Bloom", 7: "Radiant Garden", 8: "Full Blossom", 9: "Magnificent Rose", 10: "Love Master"
    };
    return levelNames[level] || "Growing";
  };

  const getProgressPercentage = () => {
    return loveScore.progress_to_next_level?.progress_percentage || 0;
  };

  const getPointsToNextLevel = () => {
    return loveScore.progress_to_next_level?.points_to_next || 0;
  };

  const getStreakMessage = () => {
    const streak_num = streak.currentStreak || 0;
    if (streak_num === 0) return "Ready to start your journey? 🌱";
    if (streak_num < 7) return `${streak_num} days strong! Keep growing 🌿`;
    if (streak_num < 30) return `${streak_num} day streak! You're blooming 🌸`;
    return `${streak_num} days of self-love! You're unstoppable 🌟`;
  };

  const handleResendVerification = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send verification email');
      }

      toast({
        title: "Email Sent! ✉️",
        description: "Check your inbox for the verification link.",
      });
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 pb-24" style={{paddingTop: subscriptionInfo?.trial?.is_trial ? '144px' : '80px'}}>
      {/* Trial Banner - Fixed at top */}
      {subscriptionInfo?.trial && (
        <TrialBanner 
          trialInfo={subscriptionInfo.trial}
          onUpgrade={() => setShowUpgradeModal(true)}
        />
      )}
      
      {/* Email Verification Banner */}
      {user && !user.email_verified && (
        <EmailVerificationBanner 
          userEmail={user.email}
          onResend={handleResendVerification}
        />
      )}
      
      <div className="max-w-lg mx-auto px-4">
        {/* Welcome Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center space-x-3 mb-2 flex-wrap">
            <h1 className="text-2xl font-serif font-bold text-gray-900">
              Welcome back, {user?.name?.split(' ')[0] || user?.name || 'Beautiful'}! 💕
            </h1>
            {user?.is_admin ? (
              <div 
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-600 text-white"
              >
                <span className="mr-1">👑</span>
                Admin
              </div>
            ) : user?.is_founding_member ? (
              <div 
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-serif font-semibold"
                style={{ 
                  background: 'linear-gradient(45deg, #FFD9E6, #FADADD)',
                  color: '#8B5D3B'
                }}
              >
                <span className="mr-1">🌟</span>
                Founding Member
              </div>
            ) : null}
            {blueprint && (
              <div 
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-serif font-semibold bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-300 text-purple-800"
              >
                <span className="mr-1">{blueprint.emoji}</span>
                The {blueprint.primary_archetype}
              </div>
            )}
          </div>
          <p className="text-gray-600 font-sans">Ready to grow your Love Score today?</p>
        </div>

        {/* Love Score Dashboard - Gamified */}
        <Card className="p-6 bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-200 mb-6 relative overflow-hidden">
          {/* Level Badge */}
          <div className="absolute top-4 right-4">
            <div className="bg-rose-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center">
              <Crown className="w-4 h-4 mr-1" />
              Level {loveScore.level || 1}
            </div>
          </div>

          <div className="text-center">
            <h3 className="font-serif font-semibold text-gray-900 mb-2">Love Score Progress</h3>
            <div className="text-4xl font-bold text-rose-600 mb-2">
              {loveScore.current_score?.toLocaleString() || '0'}
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {getLevelName(loveScore.level || 1)} • {getPointsToNextLevel()} points to next level
            </p>
            
            {/* Progress Bar with Animation */}
            <div className="bg-white rounded-full h-4 mb-4 shadow-inner">
              <div 
                className="bg-gradient-to-r from-rose-400 to-rose-600 h-4 rounded-full transition-all duration-1000 ease-out relative"
                style={{width: `${getProgressPercentage()}%`}}
              >
                <div className="absolute right-0 top-0 w-4 h-4 bg-rose-600 rounded-full shadow-lg animate-pulse"></div>
              </div>
            </div>
            
            <p className="text-xs text-rose-700 font-medium">
              {Math.round(getProgressPercentage())}% to Level {(loveScore.level || 1) + 1} ✨
            </p>
          </div>
        </Card>

        {/* Daily Progress Stats - Gamified */}
        <Card className="p-4 bg-white/80 backdrop-blur-sm border-rose-200 mb-6">
          <h3 className="font-serif font-semibold text-gray-900 mb-3 text-center">Today's Progress</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-rose-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-rose-600">{streak.currentStreak || 0}</div>
              <div className="text-xs text-gray-600 font-medium">Day Streak</div>
              <div className="text-lg">🔥</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-600">{todayStats.activities}</div>
              <div className="text-xs text-gray-600 font-medium">Activities</div>
              <div className="text-lg">✅</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-blue-600">+{todayStats.loveScoreEarned}</div>
              <div className="text-xs text-gray-600 font-medium">Love Score</div>
              <div className="text-lg">📈</div>
            </div>
          </div>
          
          {/* Streak Message */}
          <div className="mt-3 text-center">
            <p className="text-sm text-gray-600 font-sans italic">
              {getStreakMessage()}
            </p>
          </div>
        </Card>

        {/* Mood Slider - Enhanced with Labels */}
        <Card className="p-6 bg-white/80 backdrop-blur-sm border-rose-200 mb-6">
          <h3 className="font-serif font-semibold text-gray-900 mb-3 text-center">How are you feeling today?</h3>
          <p className="text-sm text-gray-500 mb-4 text-center font-sans italic">
            Click on the mood that best describes how you feel right now 💖
          </p>
          
          <div className="space-y-4">
            <div className="flex justify-between items-start px-2">
              {moodOptions.map((mood) => (
                <button
                  key={mood.value}
                  onClick={() => handleMoodSelect(mood)}
                  className={`relative flex flex-col items-center transition-all duration-300 transform ${
                    currentMood?.value === mood.value 
                      ? 'scale-110' 
                      : 'hover:scale-105'
                  }`}
                  title={mood.description}
                >
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
                    currentMood?.value === mood.value 
                      ? 'bg-rose-500 shadow-lg ring-4 ring-rose-200' 
                      : 'bg-gray-100 hover:bg-gray-200 border-2 border-gray-200 hover:border-rose-300'
                  }`}>
                    <span className="text-2xl">{mood.emoji}</span>
                  </div>
                  <span className={`text-xs mt-2 font-medium transition-colors ${
                    currentMood?.value === mood.value 
                      ? 'text-rose-600' 
                      : 'text-gray-600'
                  }`}>
                    {mood.label}
                  </span>
                </button>
              ))}
            </div>
            
            {/* Interactive Slider */}
            <div className="relative mt-4 px-2">
              <input
                type="range"
                min="1"
                max="5"
                step="1"
                value={currentMood?.value || 3}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  const selectedMood = moodOptions.find(m => m.value === value);
                  if (selectedMood) {
                    handleMoodSelect(selectedMood);
                  }
                }}
                className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer slider-thumb"
                style={{
                  background: currentMood 
                    ? `linear-gradient(to right, rgb(251 113 133) 0%, rgb(251 113 133) ${((currentMood.value - 1) / 4) * 100}%, rgb(229 231 235) ${((currentMood.value - 1) / 4) * 100}%, rgb(229 231 235) 100%)`
                    : 'rgb(229 231 235)'
                }}
              />
              <style>{`
                .slider-thumb::-webkit-slider-thumb {
                  appearance: none;
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  background: linear-gradient(135deg, rgb(244 63 94), rgb(236 72 153));
                  cursor: pointer;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                  border: 2px solid white;
                }
                .slider-thumb::-moz-range-thumb {
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  background: linear-gradient(135deg, rgb(244 63 94), rgb(236 72 153));
                  cursor: pointer;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                  border: 2px solid white;
                }
              `}</style>
            </div>
            
            {currentMood ? (
              <div className="text-center">
                <p className="text-sm text-gray-600 font-sans">
                  You're feeling: <strong className="text-rose-600">{currentMood.label} {currentMood.emoji}</strong>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {isGeneratingAffirmation ? 'Creating your personalized affirmation...' : 'Affirmation ready!'}
                </p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm text-gray-500 font-sans italic">
                  Select or slide to match your mood, then receive your personalized affirmation ✨
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Personalized Affirmation Section */}
        {isGeneratingAffirmation && (
          <Card className="p-6 bg-white/80 backdrop-blur-sm border-rose-200 mb-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-serif font-semibold text-gray-900 mb-2">
              Creating Your Personal Affirmation...
            </h3>
            <p className="text-gray-600 font-sans">
              Conchi is crafting something special just for you ✨
            </p>
          </Card>
        )}

        {currentAffirmation && !isGeneratingAffirmation && (
          <Card className="p-6 bg-white border-2 border-rose-200 mb-6">
            <h3 className="font-serif font-semibold text-gray-900 mb-4 text-center">Your Personal Affirmation</h3>
            
            {/* Affirmation Card */}
            <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg p-6 mb-4 relative">
              <div className="absolute top-2 right-2">
                <Sparkles className="w-4 h-4 text-rose-400" />
              </div>
              <p className="text-gray-800 font-medium italic text-center text-lg leading-relaxed">
                "{currentAffirmation.affirmation}"
              </p>
            </div>
            
            {/* Micro Action */}
            {microAction && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-serif font-medium text-blue-900 mb-2 flex items-center">
                  <Target className="w-4 h-4 mr-2" />
                  Today's Micro Action:
                </h4>
                <p className="text-blue-800 text-sm font-sans leading-relaxed">
                  {microAction}
                </p>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button 
                onClick={saveToLoveVault}
                className="flex-1 text-rose-500 text-sm font-medium hover:text-rose-600 py-3 px-4 border border-rose-200 rounded-lg hover:bg-rose-50 transition-colors font-sans"
              >
                💕 Save to My Love Vault
              </button>
              <button 
                onClick={markActionComplete}
                className="flex-1 text-blue-500 text-sm font-medium hover:text-blue-600 py-3 px-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors font-sans"
              >
                ✅ Action Complete
              </button>
            </div>
            
            <div className="mt-3 text-center">
              <p className="text-xs text-gray-500 font-sans">
                Personalized for your journey • Never repeated ✨
              </p>
            </div>
          </Card>
        )}

        {/* Generate Affirmation Prompt */}
        {!currentAffirmation && !isGeneratingAffirmation && (
          <Card className="p-8 bg-white/80 backdrop-blur-sm border-rose-200 text-center">
            <div className="text-5xl mb-4">✨</div>
            <h3 className="text-xl font-serif font-semibold text-gray-900 mb-3">
              Ready for Your Personal Affirmation?
            </h3>
            <p className="text-gray-600 mb-6 font-sans">
              Select your mood above to get an affirmation crafted specifically for you and your journey with Conchi's insights.
            </p>
            <div className="bg-rose-50 rounded-lg p-4">
              <p className="text-rose-800 text-sm font-sans">
                💡 <strong>Tip:</strong> Each affirmation is unique and personalized based on your conversations, mood patterns, and growth journey.
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        feature="affirmations"
      />

      {/* Trial Expired Modal */}
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
        onUpgrade={(billingPeriod) => {
          setShowTrialExpiredModal(false);
          setShowUpgradeModal(true);
        }}
      />
    </div>
  );
};

export default Home;